import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { bus, EVENTS } from './eventBus';
import { getSessionId } from './sessionManager';
import { findRegion, DEFAULT_REGION } from './regions';
import { useMockPlan, FREE_PLAN_LIMIT } from './mockPlan';

/**
 * useAgentSurface — single source of truth for the floating AgentAvatar.
 *
 * Given the current route it decides:
 *   • visible          — should the sphere render at all?
 *   • tone             — 'idle' | 'active' | 'recognized'
 *   • popoverContent   — { title, rows: [{label, value, mono?}] } tailored per surface
 *
 * The hook subscribes to the event bus on consumer routes so the sphere's
 * color reflects live detection state. On Talent Connect routes it reads
 * the mocked plan/quota. Cross-surface data flow lives here — the sphere
 * itself is dumb.
 */

const CATEGORY_LABEL = {
  all: 'All',
  everyday_basics: 'Everyday Basics',
  food_ordering: 'Food / Ordering',
};

const STATUS_LABEL = {
  idle: 'Waiting for sign',
  detecting: 'Processing…',
  'motion-detecting': 'Detecting motion',
  recognized: 'Recognized',
};

// Routes where the avatar is intentionally suppressed.
function shouldHide(pathname) {
  if (pathname === '/poc') return true;
  if (pathname === '/privacy' || pathname === '/terms') return true;
  if (pathname === '/talent-connect/candidate') return true;
  if (pathname.startsWith('/talent-connect/candidate/')) return true;
  return false;
}

function isConsumer(pathname) {
  return pathname === '/' || pathname === '/consumer' || pathname.startsWith('/consumer/');
}

function isTalentConnect(pathname) {
  return pathname.startsWith('/talent-connect');
}

export function useAgentSurface() {
  const location = useLocation();
  const [sessionId] = useState(() => getSessionId());

  // Consumer detection state, subscribed via bus. On non-consumer routes the
  // bus is silent and these stay idle — cheap.
  const [handStatus, setHandStatus] = useState('idle');
  const [speaking, setSpeaking] = useState(false);

  // Talent Connect state.
  const { plan, used } = useMockPlan();

  useEffect(() => {
    const unsubs = [
      bus.on(EVENTS.HAND_STATUS, (p) => { if (p) setHandStatus(p.status); }),
      bus.on(EVENTS.SPEAKING_START, () => setSpeaking(true)),
      bus.on(EVENTS.SPEAKING_END, () => setSpeaking(false)),
    ];
    return () => { unsubs.forEach((u) => u()); };
  }, []);

  const visible = !shouldHide(location.pathname);

  const tone =
    handStatus === 'recognized'
      ? 'recognized'
      : speaking || handStatus === 'detecting' || handStatus === 'motion-detecting'
        ? 'active'
        : 'idle';

  const popoverContent = buildPopover({
    pathname: location.pathname,
    sessionId,
    handStatus,
    speaking,
    plan,
    used,
  });

  return { visible, tone, popoverContent, pathname: location.pathname };
}

function buildPopover({ pathname, sessionId, handStatus, speaking, plan, used }) {
  const shortSid = (sessionId || '').slice(0, 8);

  // Consumer — dictionary state
  if (isConsumer(pathname)) {
    const region = findRegion(DEFAULT_REGION);
    const category = (typeof localStorage !== 'undefined' && localStorage.getItem('signvoice:category')) || 'all';
    const humanStatus = speaking ? 'Speaking…' : STATUS_LABEL[handStatus] || STATUS_LABEL.idle;
    return {
      eyebrow: 'Elite Agent Active',
      title: 'Linguistic Master',
      rows: [
        { label: 'Region', value: region?.name || '—' },
        { label: 'Sign Language', value: region?.signLang || '—' },
        { label: 'Category', value: CATEGORY_LABEL[category] || 'All' },
        { label: 'Status', value: humanStatus },
        { label: 'Session', value: shortSid, mono: true },
      ],
    };
  }

  // Talent Connect — recruiter dashboard
  if (pathname.startsWith('/talent-connect/recruiter')) {
    const activeRoom = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('talentconnect:active-room-id')) || '—';
    return {
      eyebrow: 'Talent Connect · Recruiter',
      title: 'Interview Assistant',
      rows: [
        { label: 'Plan', value: plan === 'business' ? 'Business' : 'Free' },
        { label: 'Rooms used', value: `${used} / ${plan === 'business' ? '∞' : FREE_PLAN_LIMIT}` },
        { label: 'Active room', value: activeRoom === '—' ? '—' : activeRoom, mono: activeRoom !== '—' },
        { label: 'Session', value: shortSid, mono: true },
      ],
    };
  }

  // Talent Connect — pricing
  if (pathname === '/talent-connect/pricing') {
    return {
      eyebrow: 'Talent Connect · Plans',
      title: 'Compare & Choose',
      rows: [
        { label: 'Product', value: 'Talent Connect' },
        { label: 'View', value: 'Pricing' },
        { label: 'Session', value: shortSid, mono: true },
      ],
    };
  }

  // Talent Connect — welcome / any other TC route
  if (isTalentConnect(pathname)) {
    return {
      eyebrow: 'Talent Connect',
      title: 'B2B Interview Platform',
      rows: [
        { label: 'Mode', value: 'Preview' },
        { label: 'Session', value: shortSid, mono: true },
      ],
    };
  }

  // Fallback — should be unreachable since shouldHide() blocks other routes.
  return {
    eyebrow: 'Agent Ready',
    title: 'SignVoice',
    rows: [{ label: 'Session', value: shortSid, mono: true }],
  };
}
